
import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "./useDebounce";

export function useOptimizedAdminTable<T extends { id: string }>(
  tableName: string,
  searchField: string,
  mapFunction?: (item: any) => T
) {
  const [items, setItems] = useState<T[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Debounce search query to reduce filter operations
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Memoize filtered items to avoid recalculation
  const filteredItems = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return items;
    
    const lower = debouncedSearchQuery.toLowerCase();
    return items.filter((item: any) => {
      if (item[searchField] === undefined) return true;
      return String(item[searchField]).toLowerCase().includes(lower);
    });
  }, [items, debouncedSearchQuery, searchField]);

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from(tableName)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (fetchError) {
        throw new Error(fetchError.message);
      }
      
      const processedData = mapFunction ? data.map(mapFunction) : data;
      setItems(processedData);
      return processedData;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast({ 
        title: `Error loading ${tableName}`, 
        description: errorMessage,
        variant: "destructive" 
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [tableName, toast, mapFunction]);

  // Manual refresh function
  const refreshItems = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  // Initial data fetch and realtime subscription setup
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (mounted) {
        await fetchItems();
      }
    };
    
    loadData();

    // Set up optimized realtime subscription
    const channel = supabase
      .channel(`${tableName}-changes`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: tableName }, 
          (payload) => {
            if (process.env.NODE_ENV === 'development') {
              console.log(`[useOptimizedAdminTable] Received update for ${tableName}:`, payload);
            }
            if (mounted) {
              // Debounce realtime updates to avoid excessive re-renders
              setTimeout(() => {
                if (mounted) {
                  fetchItems();
                }
              }, 500); // Increased debounce to 500ms
            }
          }
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [fetchItems, tableName]);

  return {
    items,
    filteredItems,
    setItems,
    searchQuery,
    setSearchQuery,
    fetchItems,
    refreshItems,
    isLoading,
    error
  };
}
